import type { LadderReference } from '../SpreadsTotals/ReferenceFloater';
import { classifyLineAvailability } from '@/lib/sport/lineAvailability';
import type { UserOrderSlice } from '@/hooks/useUserKalshiPOS';
import type { VenueBook, TeamSide } from '../ValueDashboard/types';
import type { GameRowsProps } from '../ValueDashboard/GameTable';
import { TwoSidedLadderBody } from '../SpreadsTotals/TwoSidedLadder';
import { bookForDisplayOnly } from '@/lib/market/bookDisplay';
import { OrderbookFreshness } from '@/components/atoms/OrderbookFreshness';

/** One market's two outcomes, separately labelled venues.
 *
 *  Built for an exact single derivative contract (#882); #887's optional
 *  moneyline experiment renders through it as well, which is why the column
 *  labels are a parameter rather than the literal YES / NO.
 *
 *  `labels` names the two outcome columns. Omitted — every existing derivative
 *  caller — they read YES and NO, as before. #887's optional moneyline
 *  experiment names the team on each column instead, because "YES" alone does
 *  not say which team's contract a price belongs to once both sides of two
 *  markets are on screen at once. */
export function DualOutcomeBooks({ reference, kalshi, polymarket, orders, makerRebatePct, onTrade, gameKey, teamSide, labels }: {
  reference?: LadderReference;
  kalshi: VenueBook | null; polymarket: VenueBook | null; orders: UserOrderSlice[];
  makerRebatePct: number; onTrade: GameRowsProps['onTradeClick']; gameKey: string; teamSide: TeamSide;
  labels?: { yes: string; no: string; caption?: string };
}) {
  const yesLabel = labels?.yes ?? 'YES';
  const noLabel = labels?.no ?? 'NO';
  return <div className="w-0 min-w-full overflow-x-auto px-2 py-2" data-dual-outcome-books="">
    <div className="mb-1 text-xs text-muted-foreground">{labels?.caption ?? 'YES and NO · five bids and five asks per book · scroll horizontally if needed'}</div>
    <div className="flex gap-4">
      {(['kalshi', 'polymarket'] as const).map(venue => {
        const book = venue === 'kalshi' ? kalshi : polymarket;
        const displayBook = bookForDisplayOnly(book);
        return <section key={venue} className={reference ? "min-w-[30rem] flex-1" : "min-w-[26rem] flex-1"} aria-label={`${venue} ${yesLabel} and ${noLabel} book`}>
          <h4 className="mb-1 text-xs font-semibold uppercase">{venue === 'kalshi' ? 'Kalshi' : 'Polymarket'}
            {book?.bookSourceValidity === 'unconfirmed' && <span className="ml-2 font-normal normal-case text-amber-400">
              {book.retainedDepth ? 'Last confirmed book' : 'Awaiting book'}</span>}
            {book?.bookSourceValidity !== 'unconfirmed' && <span className="ml-2"><OrderbookFreshness ticker={book?.marketTicker} /></span>}
          </h4>
          {reference && venue === 'kalshi' && <div className="mb-1 text-xs font-mono">Pinnacle {reference.fair?.anchorUpdatedAtMs != null && Number.isFinite(reference.fair.anchorUpdatedAtMs) && Math.abs(reference.fair.anchorUpdatedAtMs) <= 8.64e15
            ? new Date(reference.fair.anchorUpdatedAtMs).toISOString().replace('T', ' ').replace('Z', ' UTC') : 'time unavailable'}</div>}
          <TwoSidedLadderBody kalshi={displayBook} venue={venue} reference={venue === 'kalshi' && !book?.retainedDepth ? reference : undefined} yesLabel={yesLabel} noLabel={noLabel}
            availability={book?.bookSourceValidity === 'unconfirmed' ? 'stale' : classifyLineAvailability(book)} makerRebatePct={venue === 'kalshi' ? makerRebatePct : 0}
            userOrders={venue === 'kalshi' ? orders : null} presentation={{ depth: 5, layout: 'narrow' }}
            onLevelClick={(side, kind, cents) => {
              if (book?.bookSourceValidity === 'unconfirmed') return;
              onTrade?.(gameKey, teamSide, venue,
                { initialOrderSide: side, priceCents: cents, postOnly: kind === 'bid' });
            }} />
        </section>;
      })}
    </div>
  </div>;
}
